/**
 * Where every public "start using PlotScore" call-to-action points.
 *
 * Always /signup — never /app, and never the "#signup" anchor.
 *
 * /app is auth-walled (src/middleware.ts), so aiming a public CTA at it bounces
 * anonymous visitors — the entire audience of a marketing page — onto a sign-IN
 * form for an account they do not have. /signup is right for both states with no
 * client-side auth check and no loading-state flash: middleware sends already
 * signed-in users straight back out to `?redirect`.
 *
 * The "#signup" anchor is worse than it looks: it only resolves on the home page,
 * and the landing page's hashchange handler turns the click into a smooth scroll
 * across the full page rather than a navigation.
 *
 * On `?redirect=/app` — today this only steers the middleware bounce, i.e. the
 * already-signed-in visitor. The signup form itself drops the param and always
 * routes to /verify-pending, so a new member still lands on /account/dashboard
 * after confirming their email. Wiring the form to honour it is worth doing, and
 * when it happens this constant already carries the right intent.
 */
export const START_HREF = "/signup?redirect=/app";

/** The one public address. Every "talk to a human" path on the site is a
 *  mailto — there is no contact form or demo booker. */
export const CONTACT_EMAIL = "info@plotscore.app";

/**
 * Build a mailto with a pre-filled subject, so a reply lands with its context
 * already attached. Always render these as real links: /for-planners spent a
 * while naming this address in plain prose that nobody could click, under a
 * button that went somewhere else entirely.
 */
export function contactHref(subject?: string): string {
  return subject
    ? `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`
    : `mailto:${CONTACT_EMAIL}`;
}
